import { Heading } from '@chakra-ui/layout'
import { Layout } from 'components/atoms'
import { InputBuild, SelectBuild } from 'components/molecules'
import { useAuthContext } from 'contexts/auth'
import { useContractContext } from 'contexts/contract'
import { useInstitutionsContext } from 'contexts/institutions'
import { AddressKeys } from 'interfaces/Address'
import jwt_decode from 'jwt-decode'
import {
    AgentResponseDTO,
    AuthTokens,
    LocationsDTO,
    ViaCepResponseDTO
} from 'models'
import { disableUfFields } from 'pages/Contracts/utils/disableUfFields'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { getMunicipio } from 'services/municipio'
import { getCEP } from 'services/viacep'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import isDisableToEditContract from '../../StepsForm/Utils/isDisableToEditContract'
import { FormContractProps } from '../FormContract/interfaces'
import { FormFinancingProps } from '../FormFinancing/interfaces'
import { FormCreditorValue } from './form'
import { FormCreditorProps } from './interfaces'
import { FormCreditorKeys } from './types'

interface PropsCreditor {
    setAgentCNPJ: (agentCNPJ: string) => void
}

const FormCreditor = ({ setAgentCNPJ }: PropsCreditor) => {
    const {
        register,
        watch,
        formState: { errors },
        setValue,
        setError,
        trigger
    } = useFormContext<{
        creditor: FormCreditorProps
        financing: FormFinancingProps
        contract: FormContractProps
    }>()

    const { getLinkInstitutionAgent } = useInstitutionsContext()

    const { currentUser } = useAuthContext()
    const decoded =
        currentUser && (jwt_decode(currentUser.idToken) as AuthTokens)

    const { typeContractBoolean } = useContractContext()
    const [lastCep, setLastCep] = useState('')
    const [searching, setSearching] = useState(false)
    const [loading, setLoading] = useState(false)

    const [location, setLocation] = useState<LocationsDTO[]>([])
    const [agentes, setAgentes] = useState<AgentResponseDTO[]>([])

    const selectedAgent = useMemo(
        () => agentes?.find(({ cnpj }) => watch('creditor.cnpj') === cnpj),
        [watch('creditor.cnpj')]
    )

    const form = useMemo(
        () => FormCreditorValue(location, agentes),
        [location, agentes]
    )

    const hasError = (id: FormCreditorKeys) => {
        if (id.includes('endereco')) {
            return (
                errors.creditor?.endereco &&
                errors.creditor.endereco[id.split('.')[1] as AddressKeys]
            )
        } else {
            return (
                errors.creditor &&
                errors.creditor[
                    id as Extract<
                        FormCreditorKeys,
                        'cnpj' | 'telefone' | 'razaoSocial'
                    >
                ]
            )
        }
    }

    const generateCityWithCode = (
        locales: LocationsDTO[],
        localidade: string
    ) => {
        const local = `${formatter.clearCharacters(localidade).toUpperCase()}`

        const city = `${
            locales?.find(({ municipio }) => {
                return municipio === local
            })?.codigoTomDetran
        }|${local}`

        return city
    }

    const setAddress = (
        { cep, uf, bairro, logradouro }: ViaCepResponseDTO,
        municipio: string
    ) => {
        setValue('creditor.endereco.cep', cep)
        setValue('creditor.endereco.uf', uf)
        setValue('creditor.endereco.municipio', municipio)
        setValue('creditor.endereco.logradouro', logradouro)
        setValue('creditor.endereco.bairro', bairro)

        if (watch('creditor.endereco.numero')) {
            setValue(
                'creditor.endereco.numero',
                watch('creditor.endereco.numero')
            )
        }
    }

    const consultCEP = useDebounce(async (cep: string) => {
        if (cep.length < 8) return

        try {
            const responseCep = await getCEP(cep)
            const { uf, localidade, bairro, logradouro } = responseCep

            const locales = await consultMunicipio(uf)
            const municipio = generateCityWithCode(locales!, localidade)

            setAddress(responseCep, municipio)
        } catch (error) {
            setValue('creditor.endereco.cep', '')
            setValue('creditor.endereco.municipio', '')
            setError('creditor.endereco.cep', {
                type: 'validate',
                message: 'CEP Inválido'
            })
        } finally {
            setSearching(false)
        }
    }, 300)

    const handleCEP = (cep: string) => {
        const value = cep.replace(/\D/g, '')
        !typeContractBoolean && setSearching(value.length === 8)
        consultCEP(value)
    }

    const consultMunicipio = async (uf: string) => {
        if (uf) {
            setSearching(true)
            return await getMunicipio(uf)
                .then(({ data }) => {
                    setLocation(data)
                    return data
                })
                .finally(() => setSearching(false))
        } else {
            setLocation([])
        }
    }

    const cleanFieldsAddress = () => {
        const fieldsAddress: Array<AddressKeys> = [
            'cep',
            'uf',
            'bairro',
            'logradouro',
            'municipio'
        ]

        fieldsAddress.forEach(key =>
            setValue(`creditor.endereco.${key as AddressKeys}`, '')
        )
    }

    const setInitialDataFromAPIAgent = () => {
        cleanFieldsAddress()
        const cep = selectedAgent?.cep
        if (!cep) return

        handleCEP(cep)

        const numberAddress = selectedAgent?.numeroEndereco ?? ''
        const addressComplement = selectedAgent?.complemento ?? ''
        const phone = selectedAgent.telefone ?? ''
        const email = selectedAgent.email ?? ''

        setValue('creditor.endereco.cep', cep)
        setValue('creditor.email', email)
        setValue('creditor.telefone', phone)
        setValue('creditor.endereco.numero', numberAddress)
        setValue('creditor.endereco.complemento', addressComplement)
    }

    useEffect(() => {
        setValue('creditor.endereco.uf', '')
        setLastCep(watch('creditor.endereco.cep'))
        !typeContractBoolean && consultMunicipio(watch('creditor.endereco.uf'))

        if (decoded?.instituicao) {
            setLoading(true)
            typeContractBoolean && setSearching(true)
            handleCEP(watch('creditor.endereco.cep'))
            getLinkInstitutionAgent(decoded?.instituicao, {
                pagina: 1,
                limite: 30,
                uf: watch('contract.ufContrato')
            })
                .then(({ data }) => setAgentes(data))
                .finally(() => setLoading(false))
        } else {
            setAgentes([])
            setLoading(false)
            typeContractBoolean && setSearching(false)
        }
    }, [])

    useEffect(() => {
        setSearching(true)
        consultMunicipio(watch('creditor.endereco.uf')).then(() =>
            setSearching(false)
        )
    }, [watch('creditor.endereco.uf')])

    useEffect(() => {
        setAgentCNPJ(watch('creditor.cnpj'))
        setInitialDataFromAPIAgent()
    }, [watch('creditor.cnpj')])

    return (
        <Layout
            type="container"
            px={8}
            full
            display="grid"
            gap={8}
            data-testid="form-container"
        >
            <Layout type="row" data-testid="form-heading">
                <Heading size="md" textAlign={{ sm: 'center' }}>
                    Dados do credor
                </Heading>
            </Layout>
            <Layout type="row" gutter={{ y: 1.5 }} data-testid="form-fields">
                {form.map(({ layout, input, select }, key) => (
                    <Layout {...layout} key={key}>
                        {select ? (
                            <SelectBuild
                                {...select}
                                err={hasError(select.id)}
                                isSearching={select.isSearching && searching}
                                isLoading={select.isLoading && loading}
                                {...register(`creditor.${select.id}`)}
                                value={watch(`creditor.${select.id}`) as string}
                                isDisable={disableUfFields(
                                    typeContractBoolean,
                                    select,
                                    'creditor'
                                )}
                            />
                        ) : (
                            input && (
                                <InputBuild
                                    {...input}
                                    err={hasError(input.id)}
                                    {...register(`creditor.${input.id}`)}
                                    value={
                                        watch(`creditor.${input.id}`) as string
                                    }
                                    isSearching={input.isSearching && searching}
                                    onKeyUp={e => {
                                        input.id === 'endereco.cep' &&
                                            handleCEP(
                                                e.currentTarget.value as string
                                            )
                                    }}
                                    isDisable={
                                        typeContractBoolean &&
                                        isDisableToEditContract[
                                            `creditor.${input.id}`
                                        ]
                                    }
                                />
                            )
                        )}
                    </Layout>
                ))}
            </Layout>
        </Layout>
    )
}

export default FormCreditor
